<template> 
  <div class="container-fluid mt-5 pt-5">
    <div class="row starter-main">
      <TableTitle
        :title="title"
        :pageTitle="pageTitle"
        icon="fas fa-book"
        :showAction="true"
        actionLabel="Create New"
        :modal="'createNew'"
        :disabled="!canCreate('main')"
      />
      <CommonTable2
        :tableObj="tableConfig"
        :title="title"
        permissionKey="main"
        @edit="handleEdit"
        @delete="handleDelete"
        @view="handleView"
      />
    </div>

    <!-- Pagination -->
    <div
      v-if="!crud.isLoading && crud.data.value.length > 0"
      class="d-flex justify-content-end align-items-center gap-3 mt-3 mb-4 flex-wrap"
    >
      <div class="d-flex align-items-center gap-2">
        <span class="text-muted">Rows per page:</span>
        <select
          class="form-select form-select-sm rows-select"
          v-model.number="tablePagination.per_page"
          @change="changeRows"
        >
          <option v-for="n in [5, 10, 20, 50, 100]" :key="n" :value="n">
            {{ n }}
          </option>
        </select>
      </div>

      <div class="pagination-mini">
        <button
          class="page-btn prev_btn"
          :disabled="!tablePagination.has_prev"
          @click="handlePageChange(tablePagination.current_page - 1)"
        >
          «
        </button>

        <button
          v-for="(p, index) in visiblePages"
          :key="index"
          class="page-btn"
          :class="{
            'page-active': p === tablePagination.current_page,
            'disabled-dots': p === '...',
          }"
          :disabled="p === '...'"
          @click="typeof p === 'number' && handlePageChange(p)"
        >
          {{ p }}
        </button>

        <button
          class="page-btn next_btn"
          :disabled="!tablePagination.has_next"
          @click="handlePageChange(tablePagination.current_page + 1)"
        >
          »
        </button>
      </div>
    </div>
  </div>

  <!-- Create Modal -->
  <BaseModal
    id="createNew"
    :title="`Create ${pageTitle}`"
    :visible="isCreateModalOpen"
    @close="closeCreateModal"
    @save="handleSaveCreate"
    :save-button-disabled="isCreateFormValid"
  >
    <template #body>
      <BaseForm
        :fields="createFormFields"
        v-model="newItemData"
        @submit="handleSaveCreate"
        ref="createFormRef"
      />
    </template>
  </BaseModal>

  <!-- Edit/View Modal -->
  <BaseModal
    id="editModal"
    :title="`${isViewMode ? 'View' : 'Edit'} ${pageTitle}`"
    :visible="isEditModalOpen"
    @close="closeEditModal"
    @save="handleSaveEdit"
    :show-save-button="!isViewMode"
    :save-button-disabled="isEditFormValid"
  >
    <template #body>
      <BaseForm
        :fields="editFormFields"
        v-model="editingItemData"
        @submit="handleSaveEdit"
        ref="editFormRef"
      />
    </template>
  </BaseModal>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted, watch } from "vue";
import { useRouter } from "vue-router";
import { useCrud } from "@/composables/commen/useCrud";
import BaseModal from "@/components/commen/modal/BaseModal.vue";
import BaseForm, { FormField } from "@/components/commen/modal/BaseForm.vue"; 
import CommonTable2 from "@/components/commen/table/CommonTable2.vue";
import TableTitle from "@/components/commen/table/TableTitle.vue";
import { usePermissions } from "@/composables/usePermissions";

const { canCreate } = usePermissions();

const API_RESOURCE = "/common/prompt-flows";
const pageTitle = "Prompt Flow";
const title = "Prompt Flows List";
const router = useRouter();

// Use the generic CRUD composable
const crud = useCrud(API_RESOURCE);

// --- Pagination State ---
const tablePagination = ref({
  current_page: 1,
  per_page: 10,
  total: 0,
  total_pages: 0,
  has_next: false,
  has_prev: false,
});

const visiblePages = computed(() => {
  const total = tablePagination.value.total_pages;
  const current = tablePagination.value.current_page;

  if (!total || total <= 4) {
    return Array.from({ length: Math.max(1, total) }, (_, i) => i + 1);
  }

  const pages: (number | string)[] = [];
  pages.push(1);

  if (current > 4) pages.push("...");

  const windowStart = Math.max(2, current - 1);
  const windowEnd = Math.min(total - 1, current + 1);
  for (let i = windowStart; i <= windowEnd; i++) pages.push(i);

  if (current < total - 3) pages.push("...");

  pages.push(total);
  return pages;
});

// Table configuration
const tableConfig = ref({
  columns: [
    { name: "Title", field: "title", sortable: true },
    { name: "Description", field: "description", sortable: false },
    { name: "Nodes", field: "metadata.node_count", sortable: true },
    { name: "Connections", field: "metadata.connection_count", sortable: true },
    { name: "Created", field: "created_at", sortable: true, type: "date" },
    { name: "Updated", field: "updated_at", sortable: true, type: "date" },
  ],
  data: crud.data.value,
  actions: [true],
  loading: crud.isLoading.value,
  showCategoryFilter: false,
  showStatusFilter: false,
  table_width: "max-content",
});

watch(crud.data, (newVal) => {
  tableConfig.value.data = newVal;
});

watch(crud.isLoading, (newVal) => {
  tableConfig.value.loading = newVal;
});

watch(crud.pagination, (newPagination) => {
  if (newPagination && Object.keys(newPagination).length > 0) {
    tablePagination.value.current_page = newPagination.current_page || newPagination.currentPage || newPagination.page || 1;
    tablePagination.value.total_pages = newPagination.total_pages || newPagination.totalPages || Math.ceil((newPagination.total || newPagination.totalItems || 0) / tablePagination.value.per_page) || 1;
    tablePagination.value.total = newPagination.total || newPagination.totalItems || 0;
    tablePagination.value.has_prev = tablePagination.value.current_page > 1;
    tablePagination.value.has_next = tablePagination.value.current_page < tablePagination.value.total_pages;
  }
}, { deep: true });

const changeRows = async () => {
  tablePagination.value.current_page = 1;
  await crud.fetchData({ page: 1, limit: tablePagination.value.per_page });
};

const handlePageChange = async (page: number) => {
  if (typeof page !== "number" || Number.isNaN(page)) return;
  if (page < 1 || page === tablePagination.value.current_page) return;
  if (tablePagination.value.total_pages && page > tablePagination.value.total_pages) {
    return;
  }
  await crud.fetchData({ page, limit: tablePagination.value.per_page });
};

// --- Modal State ---
const isCreateModalOpen = ref(false);
const isEditModalOpen = ref(false);
const isViewMode = ref(false);

// --- Form Data ---
const newItemData = ref({
  title: "",
  description: "",
  nodes: [],
  connections: [],
  canvas_settings: {
    offset_x: 0,
    offset_y: 0,
    scale: 1,
  },
});

const editingItemData = ref<any>({
  _id: "",
  title: "",
  description: "",
  nodes: [],
  connections: [],
  canvas_settings: {
    offset_x: 0,
    offset_y: 0,
    scale: 1,
  },
});

const createFormFields: FormField[] = [
  {
    key: "title",
    label: "Title",
    type: "text",
    required: true,
    placeholder: "Enter flow title",
  },
  {
    key: "description",
    label: "Description",
    type: "textarea",
    required: false,
    placeholder: "Enter flow description",
  },
];

const editFormFields = computed<FormField[]>(() => [
  {
    key: "title",
    label: "Title",
    type: "text",
    required: true,
    placeholder: "Enter flow title",
    readonly: isViewMode.value,
  },
  {
    key: "description",
    label: "Description",
    type: "textarea",
    required: false,
    placeholder: "Enter flow description",
    readonly: isViewMode.value,
  },
]);

// --- Form Validation ---
const isCreateFormValid = computed(() => !newItemData.value.title.trim());
const isEditFormValid = computed(() => !editingItemData.value.title?.trim());

// --- Handlers ---
const handleEdit = (row: any) => {
  // Navigate to mindmap editor with flow ID
  router.push({ name: "Mindmap", query: { flowId: row._id } });
};

const handleView = (row: any) => {
  isViewMode.value = true;
  editingItemData.value = { ...row };
  isEditModalOpen.value = true;
};

const handleDelete = async (id: string) => {
  if (confirm("Are you sure you want to delete this prompt flow?")) {
    await crud.deleteItem(id);
    await crud.fetchData({ page: tablePagination.value.current_page, limit: tablePagination.value.per_page });
  }
};

const handleSaveCreate = async () => {
  if (isCreateFormValid.value) {
    return;
  }

  const result = await crud.createItem(newItemData.value);
  if (result) {
    closeCreateModal();
    newItemData.value = {
      title: "",
      description: "",
      nodes: [],
      connections: [],
      canvas_settings: {
        offset_x: 0,
        offset_y: 0,
        scale: 1,
      },
    };
    await crud.fetchData({ page: 1, limit: tablePagination.value.per_page });
  }
};

const handleSaveEdit = async () => {
  const id = editingItemData.value._id;
  if (!id) return;
  const { _id, ...updatePayload } = editingItemData.value;
  const result = await crud.updateItem(id, updatePayload);
  if (result) {
    closeEditModal();
    await crud.fetchData({ page: tablePagination.value.current_page, limit: tablePagination.value.per_page });
  }
};

const closeCreateModal = () => {
  isCreateModalOpen.value = false;
  newItemData.value = {
    title: "",
    description: "",
    nodes: [],
    connections: [],
    canvas_settings: {
      offset_x: 0,
      offset_y: 0,
      scale: 1,
    },
  };
};

const closeEditModal = () => {
  isEditModalOpen.value = false;
  isViewMode.value = false;
  editingItemData.value = {
    _id: "",
    title: "",
    description: "",
    nodes: [],
    connections: [],
    canvas_settings: {
      offset_x: 0,
      offset_y: 0,
      scale: 1,
    },
  };
};

// Lifecycle
onMounted(async () => {
  await crud.fetchData({ page: tablePagination.value.current_page, limit: tablePagination.value.per_page });
});
</script>

<style scoped>
.rows-select {
  width: 82px;
  padding: 8px 17px;
  font-size: 0.85rem;
  background-color: transparent;
}

.pagination-mini {
  display: flex;
  align-items: center;
  border-radius: 6px;
  overflow: hidden;
}

.page-btn {
  border: none;
  background: transparent;
  font-size: 1rem;
  padding: 7px 12px;
  cursor: pointer;
  color: #6c757d;
  border-radius: 0;
  transition: 0.2s;
  border: 1px solid #dee2e6;
  outline: none;
}

.page-btn:first-child {
  border-top-left-radius: 6px;
  border-bottom-left-radius: 6px;
}

.page-btn:last-child {
  border-top-right-radius: 6px;
  border-bottom-right-radius: 6px;
}

.page-btn:hover {
  background: #e9ecef;
}

.page-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.page-active {
  background: #21c1d6;
  color: #fff;
  padding: 7px 12px;
  font-weight: 600;
  min-width: 28px;
  text-align: center;
  display: inline-block;
  border: 1px solid #21c1d6;
}

.page-active:hover {
  background: #21c1d6;
}

.prev_btn,
.next_btn {
  color: #21c1d6;
}
</style>
